import React, {useMemo} from 'react';
import {clsx} from 'clsx';

import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {filterToBookmarkableEventItem} from 'common/query-builder/query-block/events-menu/util';
import {isSamePropertyReference} from 'common/query-builder/query-block/properties-menu/util';
import {PropertyType, ReportName, ResourceType} from 'common/report/constants';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {useTranslation} from 'common/i18n';

import {EntryRowProps} from '../row-types/types';
import {
  Action,
  Entry,
  EntryPart,
  isMetricEntry,
  isPropertyEntry,
  isSegmentEntry,
  QuerySegmentEntry,
  QuerySuperEntry,
} from '../types';
import {getIconForEntry} from '../util/entry-icon';
import {getSegmentationProperty} from '../row-types/row-util';

export function RowSelector(
  props: EntryRowProps<Entry> & {
    contextPanelParentElement: HTMLElement;
    isPlaceholder: boolean;
    selectorIcon?: string;
    handleSelect?: (detail: any) => void;
    handleCreate?: () => void;
    selected?: any;
  },
) {
  const selectorIcon = props.selectorIcon || getIconForEntry(props.entry);
  const {t} = useTranslation();

  const hasProperty = `property` in props.entry && props.entry.property?.value;
  const selected = props.selected ?? (isMetricEntry(props.entry) ? null : props.entry.event || null);

  const labelOverride = useMemo<string | undefined>(() => {
    const isSet = selected?.value;

    if (!isSet) {
      if ([ReportName.Funnels, ReportName.Flows].includes(props.sectionProps.reportName)) {
        return t(`query-builder.select-step`);
      } else {
        return t(`query-builder.select-event`);
      }
    }

    // fallback to default query-block label behavior
    return undefined;
  }, [selected]);

  const onEventSelected = (detail) => {
    const {item: value, trackingProps, queryReason} = detail;

    // TODO(CC): Handle entry event selected once we can add metric entry
    if (isMetricEntry(props.entry)) {
      return;
    }

    // TODO: This implies that every non-property is an event. Confirm this and implement strong type asserting.
    const event =
      isPropertyEntry(props.entry) || props.entry.type === ResourceType.Cohort
        ? value
        : filterToBookmarkableEventItem(value as EventItem);
    const entry = {...props.entry, event} as QuerySuperEntry;

    const isPlaceholder = props.wouldBePlaceholder(entry);

    let action;
    if (props.isStaged && !isPlaceholder) {
      action = Action.Add;
    } else {
      action = Action.Edit;

      if (isSegmentEntry(entry) && entry.customBucket) {
        const existingProperty = getSegmentationProperty(props.entry as QuerySegmentEntry);
        const updatedProperty = getSegmentationProperty(entry);
        if (
          updatedProperty.propertyType !== PropertyType.Number ||
          !isSamePropertyReference(existingProperty, updatedProperty)
        ) {
          entry.customBucket = null;
        }
      } else {
        // removes invalid first time filters
        const rowFilteringCapabilities = props.getFilteringCapabilities(entry);
        if (!rowFilteringCapabilities.canApplyFirstTimeEventFilter) {
          entry.filters = entry.filters.filter((filter) => !isFirstTimeEventFilter(filter));
        }
      }
    }

    props.updateEntry(action, entry, {trackingProps, queryReason});

    if (isPlaceholder) {
      props.setActivePart(EntryPart.NumericProperty, `open`);
    }
  };

  return (
    <QueryBlockReact
      key={`entry-selector-${props.index}${props.isPlaceholder ? `-placeholder` : ``}`}
      className={clsx(`entry-selector`, {
        'is-disabled': isMetricEntry(props.entry) ? false : props.entry.readOnlyEvent,
        'is-placeholder': props.isPlaceholder,
        'has-property': !props.hasNewUI && hasProperty,
      })}
      {...props.queryBlockConfig}
      label={labelOverride}
      autofocus={props.index === 0 && props.sectionProps.autofocus && props.isPlaceholder && !props.isFirstEntryStaged}
      buttonSize={props.sectionProps.buttonTheme === ButtonTheme.Browse ? ButtonSize.Large : ButtonSize.Medium}
      elref='entrySelector'
      open={props.isPlaceholder && props.activePart === EntryPart.Value}
      dataGroupId={props.sectionProps.dataGroupId}
      enableEnhancedListFilters={props.sectionProps.enableEnhancedListFilters}
      enableWorkspaceFilterRestrictions={props.sectionProps.enableWorkspaceFilterRestrictions}
      excludeCustomEvents={props.sectionProps.excludeCustomEvents}
      hideEditControls={props.sectionProps.hideEditControls}
      inlineCohortsOverride={props.sectionProps.inlineCohortsOverride}
      projectHasCohorts={props.sectionProps.projectHasCohorts}
      projectId={props.sectionProps.projectId}
      selected={selected}
      theme={
        props.sectionProps.buttonTheme ||
        (props.hasNewUI
          ? ButtonTheme.QueryBuilder
          : props.entry.readOnlyEvent
            ? ButtonTheme.Tertiary
            : ButtonTheme.Select)
      }
      trackName={props.sectionProps.trackName}
      selectorIcon={props.sectionProps.enableEventIcon || !props.hasNewUI ? selectorIcon : ``}
      selectorType={SelectorType.Button}
      userId={props.sectionProps.userId}
      disabled={props.sectionProps.disabled}
      workspaceId={props.sectionProps.workspaceId}
      prefixLabel={props.entry.prefixLabel}
      contextPanelParentElement={props.contextPanelParentElement}
      onClose={() => {
        props.setActivePart(EntryPart.Value, `close`);
        props.onEntrySelectorDropdownStateChanged(false);
      }}
      onSavedCustomEvent={props.propagateEvent}
      onOpen={() => {
        props.setActivePart(EntryPart.Value, `open`);
        props.onEntrySelectorDropdownStateChanged(true);
      }}
      onRemove={() => props.removeEntry()}
      onSelect={props.handleSelect ?? onEventSelected}
      onTrack={props.trackEntrySelectorEvent}
      onCreate={props.handleCreate}
    />
  );
}
